import React, { useEffect, useState } from 'react';
import GlobalNavigation from '@components/GlobalNavigation';
import CardDisplay from '@components/CardDisplay';
import ExpressCart from '@components/ExpressCart';
import QuantitySelector from '@components/QuantitySelector';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import GlobalFooter from '@components/GlobalFooter';
import GlobalFooterWithSellerInfo from '@components/GlobalFooterWithSellerInfo';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { inject, observer } from 'mobx-react';
import { Typography, Grid, Button, Divider } from '@material-ui/core';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import Router, { useRouter } from 'next/router';
import _get from 'lodash/get';
import dayjs from 'dayjs';
import request from '@utils/request';
import _isEmpty from 'lodash/isEmpty';
import AdditionalOptions from '@components/AdditionalOptions';
import { isLoggedIn } from '@utils/account';
import CustomSnackbar from '@components/CustomSnackbar';
import Carousel from '@components/Carousel';
import _find from 'lodash/find';
import { getStoreDomain, getResponseErrors, getFinalLocale } from '@utils/store';
import { localSubdomain } from '@constants/index';
import AdSense from 'react-ssr-adsense';
import useWindowSize from '@hooks/useWindowSize';
import ProductItem from '@components/ProductItem';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import BoxModal from '@components/BoxModal';
import ConfigureBundleProductForm from '@components/ConfigureBundleProductForm';
import BundleCriteria from '@components/BundleCriteria';
import { isShowAdSense } from '../../utils/store';

const useStyles = makeStyles(theme => ({
  root: {},
  arrowBack: {
    position: 'relative',
    top: 7,
  },
  orderSummaryCardRoot: {
    position: 'sticky',
    top: 0,
  },
  productContentContainer: {
    padding: '22px 20px',
  },
  imageWrapper: {
    textAlign: 'center',
    backgroundColor: '#EBEDF2',
    lineHeight: 0,
    position: 'relative',
    width: '100%',
    height: 330,
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      height: 430,
    },
  },
  backLink: {
    color: '#076BD1',
    cursor: 'pointer',
  },
  backButtonWrapper: {
    backgroundColor: '#fff',
    borderRadius: 30,
    position: 'absolute',
    top: 15,
    left: 15,
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      top: 15,
      left: 15,
    },
  },
  productContainer: {
    position: 'relative',
  },
  buttonClassDisplayRoot: {
    textAlign: 'center',
  },
  addToCartRoot: {
    [theme.breakpoints.up('md')]: {
      minWidth: 345,
    },
  },
  orderSummaryWrapper: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  addToCartWrapper: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  actionWrapper: {
    zIndex: 100,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    padding: '12.5px 24.5px',
    backgroundColor: '#fff',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  buttonRoot: {
    width: '100%',
    display: 'flex',
  },
  buttonLabel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  variantWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  radioLabel: {
    width: '100%',
  },
  adsWrapper: {
    position: 'relative',
    padding: '20px 0',
  },
  bundleDealEditActionWrapper: {
    borderRadius: 10,
    padding: '0px 12px 10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    color: '#076BD1',
    cursor: 'pointer',
  },
  bundleDealEditActionContainer: {
    backgroundColor: '#FEFAEB',
    borderRadius: 10,
    padding: '10px 12px',
    marginTop: 10,
  },
  bundleDealLabel: {
    color: '#076BD1',
    borderRadius: 10,
    border: '1px solid #076BD1',
    padding: '4.5px 9px',
    fontSize: 12,
    lineHeight: '12px',
    fontFamily: [
      '"CircularStd-Medium"',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  bundleItemWrapper: {
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: '10px 11px',
    marginTop: 8,
  },
  alertWrapper: {
    backgroundColor: '#d73a49',
    color: '#fff',
    padding: '8px 25px 10px 25px',
    borderRadius: 10,
  },
}));

const Index = inject('store')(
  observer(
    ({
      webStore,
      subdomain,
      product,
      domainPath,
      locale,
      store: {
        orderStore,
        orderStore: { tempOrder = {}, order = {}, loadingOrder = true },
        accountStore: { me = {} },
      },
    }) => {
      const { t } = useTranslation('common');
      const classes = useStyles();
      const router = useRouter();
      const { width } = useWindowSize();
      const [quantity, setQuantity] = useState(1);
      const [snackBarType, setSnackBarType] = useState('error');
      const [errorMessage, setErrorMessage] = useState('');
      const [selectedVariant, setSelectedVariant] = useState({});
      const [selectedOptions, setSelectedOptions] = useState([]);
      const [isOOS, setIsOOS] = useState(false);
      const [subItems, setSubItems] = useState([]);
      const [bundlePrice, setBundlePrice] = useState(0);
      const [selectedOrderRow, setSelectedOrderRow] = useState(null);

      useEffect(() => {
        if (
          _get(product, 'track_quantity', false) &&
          _get(product, 'ignore_oos', false) === false
        ) {
          if (_get(product, 'has_variation', false)) {
            let firstAvailableOption = null;
            _get(product, 'details', []).map((data, i) => {
              if (_get(data, 'quantity') > 0 && firstAvailableOption === null) {
                firstAvailableOption = i;
              }
            });
            if (firstAvailableOption !== null) {
              setIsOOS(false);
              setSelectedVariant(_get(product, `details.${firstAvailableOption}`));
            } else {
              setSelectedVariant(_get(product, `details.0`));
              setIsOOS(true);
            }
          } else {
            if (_get(product, 'details.0.quantity') > 0) {
              setIsOOS(false);
            } else {
              setIsOOS(true);
            }
          }
        } else {
          if (_get(product, 'has_variation', false)) {
            setSelectedVariant(_get(product, `details.0`));
          }
        }
        if (_get(product, 'product_type') === 'group') {
          setSubItems(_get(product, 'sub_products', []));
        }
        setQuantity(_get(product, 'min_qty_per_customer') || 1);
      }, [product]);

      useEffect(() => {
        if (!_isEmpty(me)) {
          orderStore.getCart({ subdomain });
        }
      }, [me, subdomain, orderStore]);

      const addToCart = ({ withRedirect = false }) => {
        setSnackBarType('');
        setErrorMessage('');
        const product_detail_id = _get(product, 'has_variation', false)
          ? _get(selectedVariant, 'id')
          : _get(product, 'details.0.id', false);
        let unit_price = 0;
        if (_get(product, 'product_type') === 'bundle') {
          unit_price = bundlePrice;
        } else {
          unit_price = _get(product, 'has_variation', false)
            ? _get(selectedVariant, 'price')
            : _get(product, 'details.0.price', false);
        }
        const name = _get(product, 'has_variation', false)
          ? `${_get(product, 'name', '')} / ${_get(selectedVariant, 'name')}`
          : _get(product, 'details.0.name', false);
        let orderItem = {
          images: _get(product, 'images', []),
          limit_qty_per_customer: _get(product, 'limit_qty_per_customer'),
          min_qty_per_customer: _get(product, 'min_qty_per_customer'),
          id: Math.random()
            .toString(36)
            .substr(2, 9),
          name,
          product_detail_id,
          product_type: _get(product, 'product_type'),
          quantity,
          price: unit_price,
          row_total: quantity * unit_price,
          created_at: dayjs().toString(),
          sub_items: [],
          product_info: {
            custom_options: [],
          },
        };
        if (product && product.custom_options && product.custom_options.length > 0) {
          orderItem.product_info.custom_options = product.custom_options;
        }
        if (selectedOptions && selectedOptions.length > 0) {
          orderItem.options = selectedOptions;
        }
        if (subItems.length > 0) {
          orderItem.sub_items = subItems;
        }
        const storeDomain = getStoreDomain(subdomain);
        if (isLoggedIn()) {
          orderStore
            .addToCart({
              subdomain,
              order_items: [orderItem],
            })
            .then(res => {
              const errors = _get(res, 'data.errors', []);
              if (errors.length > 0) {
                setSnackBarType('error');
                setErrorMessage(_get(errors, '0'));
              } else {
                if (withRedirect) {
                  router.push(`${storeDomain}/store`);
                }
              }
            });
        } else {
          orderStore.addToTempOrder(orderItem);
          if (withRedirect) {
            router.push(`${storeDomain}/store`);
          }
        }
      };

      const getCurrentPrice = () => {
        if (_get(product, 'product_type') === 'bundle' && bundlePrice > 0) {
          return bundlePrice.toFixed(2);
        }
        const unit_price = _get(product, 'has_variation', false)
          ? _get(selectedVariant, 'price')
          : _get(product, 'details.0.price', false);
        return (quantity * unit_price).toFixed(2);
      };

      const placeOrder = () => {
        // if has logged in
        if (!_isEmpty(me)) {
          if (_get(order, 'id')) {
            router.push(`${domainPath}/cart?id=${_get(order, 'id')}`);
          } else {
            console.error('No order Id');
          }
        } else {
          router.push(`${domainPath}/permission?subdomain=${subdomain}`);
        }
      };

      const onChangeCartItem = x => {
        setErrorMessage('');
        setSnackBarType('');
        orderStore
          .putOrder({
            order_id: _get(order, 'id'),
            order_items: x,
          })
          .then(res => {
            if (_get(res, 'status', false) === false) {
              setSnackBarType('error');
              const errors = getResponseErrors(res);
              setErrorMessage(errors);
            }
          });
      };

      const onSelectVariant = e => {
        const variants = _get(product, 'details', []);
        const found = _find(variants, ({ id }) => {
          return id == e.target.value;
        });
        setSelectedVariant(found);
      };

      const onClickEditBundleSubItems = e => {
        setSelectedOrderRow(e);
      };

      const onSubmitConfigureBundleProductForm = e => {
        orderStore
          .getCalculateBundlePrice({ product_detail_id: _get(product, 'details.0.id'), items: e })
          .then(res => {
            if (_get(res, 'data.status', false)) {
              const price = _get(res, 'data.data.price', 0);
              const discount = _get(res, 'data.data.discount', 0);
              const finalPrice = price - discount;
              setBundlePrice(finalPrice);
            }
            setSubItems(e);
            setSelectedOrderRow(null);
          });
      };
      const getTotalSelectedQuantity = (_selectedSubProducts = []) => {
        let totalQuantity = 0;
        if (_selectedSubProducts.length > 0) {
          _selectedSubProducts.map(({ quantity = 0 }) => (totalQuantity += quantity));
        }
        return totalQuantity;
      };
      const totalSelectedQuantity = subItems.length > 0 ? getTotalSelectedQuantity(subItems) : 0;
      const currency = _get(webStore, 'store_currency', 'MYR');

      return (
        <div>
          <Head>
            <title>{_get(product, 'name')} - SellOnLive</title>
            <meta
              property="og:title"
              content={`${_get(product, 'name', '')} - ${_get(webStore, 'title', '')}`}
            />
            <meta property="og:description" content={_get(product, 'description', '')} />
            <meta property="og:image" content={_get(webStore, 'facebook_page.cover_url', '')} />
            <meta
              property="og:url"
              content={`https://${subdomain}.sellonlive.tech/p/${_get(product, 'slug')}`}
            />
          </Head>
          <CustomSnackbar
            type={snackBarType}
            errorMessage={errorMessage}
            closeCallback={() => setErrorMessage('')}
          />
          <GlobalNavigation isShowLogo={false} subdomain={subdomain} />
          <div className="container">
            {isShowAdSense({
              facebookPageId: webStore?.facebook_page?.id,
            }) ? (
              <>
                {width > 768 ? (
                  <div className={classes.adsWrapper}>
                    <AdSense
                      client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                      slot="9251918266"
                      style={{
                        display: 'block',
                        width: 1024,
                        height: 100,
                        margin: '0 auto',
                        textAlign: 'center',
                      }}
                      format=""
                    />
                  </div>
                ) : (
                  <div className={classes.adsWrapper}>
                    <AdSense
                      client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                      slot="6073707693"
                      style={{ display: 'block', width: 320, height: 100, margin: '0 auto' }}
                      format=""
                    />
                  </div>
                )}
              </>
            ) : (
              <br />
            )}
            <Grid container className={classes.contentContainer} spacing={3}>
              <Grid item xs={12}>
                <div
                  className={classes.backLink}
                  onClick={() => {
                    if (document.referrer.indexOf(window.location.host) >= 0) {
                      window.history.back();
                    } else {
                      Router.push('/store');
                    }
                  }}
                >
                  <KeyboardBackspaceIcon className={classes.arrowBack} />{' '}
                  {t('back-to-all-products')}
                </div>
                {/* {isFromStore ? (
                  <div className={classes.backLink} onClick={() => window.history.back()}>
                    <KeyboardBackspaceIcon className={classes.arrowBack} />{' '}
                    {t('back-to-all-products')}
                  </div>
                ) : (
                  <Link href={`${getStoreDomain(subdomain)}/store`}>
                    <a className={classes.backLink}>
                      <KeyboardBackspaceIcon className={classes.arrowBack} />{' '}
                      {t('back-to-all-products')}
                    </a>
                  </Link>
                )} */}
              </Grid>
              <Grid item xs={12} md={8}>
                <CardDisplay noPadding={true}>
                  <Carousel images={_get(product, 'images', [])} />
                  <div className={classes.productContentContainer}>
                    <Typography variant="h1" paragraph>
                      <b>{_get(product, 'name')}</b>
                    </Typography>
                    <Typography paragraph>
                      {_get(product, 'description')
                        ? _get(product, 'description')
                            .split('\n')
                            .map(function(item, idx) {
                              return (
                                <span key={idx}>
                                  {item}
                                  <br />
                                </span>
                              );
                            })
                        : ''}
                    </Typography>
                    {_get(product, 'product_type') === 'bundle' && bundlePrice > 0 ? (
                      <Typography color="primary" variant="h2" align="right">
                        {currency}
                        {bundlePrice.toFixed(2)}
                      </Typography>
                    ) : null}
                    {_get(product, 'product_type') !== 'bundle' ? (
                      <Typography color="primary" variant="h2" align="right">
                        {_get(product, 'price')}
                      </Typography>
                    ) : null}
                    {_get(product, 'product_type') === 'group' ? (
                      <>
                        <div className={classes.bundleDealEditActionContainer}>
                          <div className={classes.bundleDealEditActionWrapper}>
                            <div className={classes.bundleDealLabel}>{t('group-deal')}</div>
                          </div>
                          {subItems.length > 0
                            ? subItems.map((data, i) => {
                                const name = _get(data, 'product.has_variation')
                                  ? `${_get(data, 'product.name')} / ${_get(data, 'name')}`
                                  : _get(data, 'name');
                                return (
                                  <Grid
                                    container
                                    key={i}
                                    className={classes.bundleItemWrapper}
                                    justifyContent="space-between"
                                  >
                                    <Grid item>{name}</Grid>
                                    <Grid item>
                                      &nbsp;&nbsp;x {_get(data, 'quantity_in_group')}
                                    </Grid>
                                  </Grid>
                                );
                              })
                            : ''}
                        </div>
                      </>
                    ) : null}
                    {_get(product, 'product_type') === 'bundle' ? (
                      <>
                        <div className={classes.bundleDealEditActionContainer}>
                          <div
                            className={classes.bundleDealEditActionWrapper}
                            onClick={() => onClickEditBundleSubItems(product)}
                          >
                            <div className={classes.bundleDealLabel}>{t('bundle-deal')}</div>
                            <div>
                              <u>{t('choose-bundle-items')}</u> >
                            </div>
                          </div>
                          {_get(product, 'bundle_discount_type') &&
                          _get(product, 'bundle_criteria', []).length > 0 ? (
                            <BundleCriteria
                              bundle_max={_get(product, 'bundle_max')}
                              bundle_discount_type={_get(product, 'bundle_discount_type')}
                              bundle_criteria={_get(product, 'bundle_criteria', [])}
                            />
                          ) : null}
                          {_get(product, 'bundle_min') &&
                          _get(product, 'bundle_min') > totalSelectedQuantity ? (
                            <p className={classes.alertWrapper}>
                              {t('at-least-select-before-add-to-cart', {
                                value: _get(product, 'bundle_min'),
                              })}
                            </p>
                          ) : null}
                          {subItems.length > 0
                            ? subItems.map((data, i) => {
                                const name = _get(data, 'product.has_variation')
                                  ? `${_get(data, 'product.name')} / ${_get(data, 'name')}`
                                  : _get(data, 'name');
                                return (
                                  <Grid
                                    container
                                    key={i}
                                    className={classes.bundleItemWrapper}
                                    justifyContent="space-between"
                                  >
                                    <Grid item>{name}</Grid>
                                    <Grid item>&nbsp;&nbsp;x {_get(data, 'quantity')}</Grid>
                                  </Grid>
                                );
                              })
                            : ''}
                        </div>
                      </>
                    ) : null}
                  </div>
                </CardDisplay>
                <br />
                {!_isEmpty(selectedVariant) &&
                _get(product, 'has_variation', false) &&
                _get(product, 'details', []).length > 0 ? (
                  <>
                    <CardDisplay title={t('choose-your-selection')}>
                      <RadioGroup
                        name="variants"
                        onChange={x => onSelectVariant(x)}
                        value={_get(selectedVariant, 'id')}
                      >
                        {_get(product, 'details', []).map((item, i) => (
                          <FormControlLabel
                            key={i}
                            value={_get(item, 'id')}
                            control={<Radio color="primary" />}
                            label={
                              <div className={classes.variantWrapper}>
                                <div className={classes.variantName}>
                                  {_get(item, 'name')}{' '}
                                  {_get(product, 'track_quantity', false) &&
                                  _get(product, 'ignore_oos', false) === false &&
                                  _get(item, 'quantity') === 0
                                    ? t('out-of-stock')
                                    : null}
                                </div>
                                <div className={classes.variantPrice}>
                                  {currency} {_get(item, 'price').toFixed(2)}
                                </div>
                              </div>
                            }
                            disabled={
                              _get(product, 'track_quantity', false) &&
                              _get(product, 'ignore_oos', false) === false &&
                              _get(item, 'quantity') === 0
                            }
                            classes={{
                              root: classes.radioRoot,
                              label: classes.radioLabel,
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </CardDisplay>
                  </>
                ) : null}
                {_get(product, 'custom_options', []).length > 0 ? (
                  <CardDisplay title={t('additional-options')}>
                    <AdditionalOptions
                      isShowTitle={false}
                      custom_options={_get(product, 'custom_options', [])}
                      selected_options={selectedOptions}
                      onSelectCustomOption={options => setSelectedOptions(options)}
                    />
                  </CardDisplay>
                ) : null}
                {isOOS || _get(product, 'product_type') === 'bundle' ? null : (
                  <>
                    <br />
                    <CardDisplay title={t('quantity')}>
                      <QuantitySelector
                        fontSize="large"
                        onQtyChanged={e => setQuantity(e)}
                        value={quantity}
                        isAllowToChangeQuantity={true}
                        limit_qty_per_customer={_get(product, 'limit_qty_per_customer')}
                        min_qty_per_customer={_get(product, 'min_qty_per_customer')}
                      />
                    </CardDisplay>
                  </>
                )}
                <div className={classes.addToCartWrapper}>
                  <br />
                  <CardDisplay
                    classes={{
                      root: classes.buttonClassDisplayRoot,
                    }}
                  >
                    {isOOS ? (
                      <Button
                        color="primary"
                        classes={{
                          root: classes.addToCartRoot,
                        }}
                        variant="contained"
                        size="large"
                        disabled={true}
                      >
                        {t('item-out-of-stock')}
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        classes={{
                          root: classes.addToCartRoot,
                        }}
                        disabled={
                          loadingOrder ||
                          (_get(product, 'product_type') === 'bundle' && bundlePrice === 0)
                        }
                        onClick={() => addToCart({ withRedirect: false })}
                      >
                        {loadingOrder ? t('loading') : t('add-to-cart')}
                      </Button>
                    )}
                  </CardDisplay>
                </div>
              </Grid>
              <Grid item className={classes.orderSummaryWrapper} xs="auto" md={4}>
                <div className={classes.orderSummaryCardRoot}>
                  <CardDisplay title={t('order-summary')}>
                    <ExpressCart
                      isAllowToChangeQuantity={_get(webStore, 'allow_change_cart', false)}
                      isAllowToChangeWebStoreQuantity={_get(
                        webStore,
                        'allow_change_webstore_cart',
                        false
                      )}
                      isStoreAllowHoldShipping={false}
                      currency={currency}
                      data={
                        isLoggedIn()
                          ? _get(order, 'order_items', [])
                          : _get(tempOrder, 'order_items', [])
                      }
                      subTotal={
                        isLoggedIn() ? _get(order, 'subtotal', 0) : _get(tempOrder, 'subtotal', 0)
                      }
                      onChangeCartItem={e =>
                        isLoggedIn() ? onChangeCartItem(e) : orderStore.updateOrderItems(e)
                      }
                      loading={false}
                      onClickPlaceOrder={placeOrder}
                    />
                  </CardDisplay>
                  {isShowAdSense({
                    facebookPageId: webStore?.facebook_page?.id,
                  }) && width > 1024 ? (
                    <>
                      <br />
                      <AdSense
                        client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                        slot="7414709526"
                        style={{ display: 'block' }}
                        format="auto"
                        responsive="true"
                      />
                    </>
                  ) : null}
                  <br />
                </div>
              </Grid>
            </Grid>
            <br />
            <Divider style={{ backgroundColor: '#EBEDF2' }} />
            {width > 768 ? null : isShowAdSense({
                facebookPageId: webStore?.facebook_page?.id,
              }) ? (
              <div className={classes.adsWrapper}>
                <AdSense
                  client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                  slot="6704263083"
                  style={{ display: 'block', width: 320, height: 250, margin: '0 auto' }}
                  format=""
                />
                <br />
              </div>
            ) : null}
            {_get(product, 'suggestions', []).length > 0 ? (
              <>
                <br />
                <CardDisplay title={t('recommended-products')}>
                  <Grid container spacing={2}>
                    {_get(product, 'suggestions', []).map(
                      ({ is_available = true, name, image, description, slug, price }, i) => (
                        <Grid item xs={12} md={4} key={i}>
                          <ProductItem
                            price={price}
                            productImage={image}
                            productName={name}
                            description={description}
                            slug={slug}
                            is_available={is_available}
                            locale={locale}
                          />
                        </Grid>
                      )
                    )}
                  </Grid>
                </CardDisplay>
                <br />
              </>
            ) : null}
          </div>
          {_get(webStore, 'facebook_page.id') && _get(webStore, 'facebook_page.name') ? (
            <GlobalFooterWithSellerInfo
              facebookPageId={_get(webStore, 'facebook_page.id')}
              facebookPageName={_get(webStore, 'facebook_page.name')}
            />
          ) : (
            <GlobalFooter />
          )}
          <div className={classes.actionWrapper}>
            {isOOS ? (
              <Button
                color="primary"
                classes={{
                  root: classes.buttonRoot,
                }}
                variant="contained"
                size="large"
                disabled={true}
              >
                {t('item-out-of-stock')}
              </Button>
            ) : (
              <Button
                color="primary"
                variant="contained"
                size="large"
                classes={{
                  root: classes.buttonRoot,
                  label: classes.buttonLabel,
                }}
                disabled={
                  loadingOrder || (_get(product, 'product_type') === 'bundle' && bundlePrice === 0)
                }
                onClick={() => addToCart({ withRedirect: true })}
              >
                {loadingOrder ? t('loading') : t('add-to-cart')}
                <div>{`${currency}${getCurrentPrice()}`}</div>
              </Button>
            )}
          </div>
          <BoxModal
            open={selectedOrderRow}
            heading={t('choose-bundle-items')}
            handleClose={() => setSelectedOrderRow(null)}
          >
            <ConfigureBundleProductForm
              product_detail_id={_get(product, 'details.0.id')}
              subItems={subItems}
              onSubmit={selectedSubProducts => {
                onSubmitConfigureBundleProductForm(selectedSubProducts);
              }}
              onCancel={() => {
                setSelectedOrderRow(null);
              }}
              bundle_criteria={_get(product, 'bundle_criteria', [])}
              bundle_discount_type={_get(product, 'bundle_discount_type')}
              bundleMin={_get(product, 'bundle_min', 0)}
              bundleMax={_get(product, 'bundle_max', 0)}
            />
          </BoxModal>
        </div>
      );
    }
  )
);

export const getServerSideProps = async ({ locale, ...props }) => {
  const host = _get(props, 'req.headers.host', '');
  const localeFromCookies = _get(props, 'req.cookies.language');
  let subdomain = host ? host.split('.')[0] : '';

  if (process.env.NEXT_PUBLIC_COOKIE_DOMAIN === 'localhost') {
    subdomain = localSubdomain;
  }
  const slug = _get(props, 'params.slug');
  let redirect = false;
  let product = {};
  let webStore = {};
  if (subdomain && slug) {
    webStore = await request(`${process.env.NEXT_PUBLIC_API_SERVER}/store/${subdomain}`, {
      method: 'GET',
    }).then(res => {
      return _get(res, 'data.data', {});
    });
    product = await request(
      `${process.env.NEXT_PUBLIC_API_SERVER}/store/${subdomain}/products/${encodeURI(slug)}`,
      {
        method: 'GET',
      }
    ).then(res => {
      return _get(res, 'data.data', {});
    });
  }

  const { finalLocale, needRedirect } = getFinalLocale({
    localeFromCookies,
    localeFromServer: locale,
    storeLocale: _get(webStore, 'auto_reply_language', 'en'),
  });
  if (needRedirect) {
    redirect = {
      destination: `/${finalLocale}/p/${encodeURIComponent(_get(props, 'params.slug'))}`,
    };
  }
  return {
    notFound: _isEmpty(webStore) || _isEmpty(product),
    redirect,
    props: {
      webStore,
      subdomain,
      product,
      locale: finalLocale,
      domainPath:
        locale === 'en'
          ? process.env.NEXT_PUBLIC_DOMAIN_URL
          : `${process.env.NEXT_PUBLIC_DOMAIN_URL}/${locale}`,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default Index;
